import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import { Flame, Clock, CheckCircle2, PlayCircle, Sparkles } from 'lucide-react';
import Badges from '@/components/Badges';
import StreakBanner from '@/components/StreakBanner';
import OnboardingModal from '@/components/OnboardingModal';
import SongOfWeekHero from '@/components/SongOfWeekHero';
import { useT } from '@/context/LocaleContext';

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const [progress, setProgress] = useState({ items: [], stats: {} });
  const [songs, setSongs] = useState([]);
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, b] = await Promise.all([
          api.get('/progress'),
          api.get('/songs?difficulty=beginner'),
          api.get('/badges'),
        ]);
        setProgress(p.data);
        setSongs(s.data.slice(0, 4));
        setBadges(b.data.badges);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const stats = progress.stats || {};

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl">
        <OnboardingModal />
        <StreakBanner />
        <div className="mb-10 rise">
          <div className="overline text-orange-400 mb-2">{t('dashboard.welcome')}</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-none">
            {t('dashboard.hello')} <em className="not-italic text-orange-500">{user?.name?.split(' ')[0] || 'musicien'}</em>.
          </h1>
          <p className="mt-3 text-stone-400 max-w-xl">{t('dashboard.subtitle')}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Flame} label={t('dashboard.streak')} value={`${stats.streak_days || 0} j`} tid="stat-streak" />
          <StatCard icon={Clock} label={t('dashboard.minutes')} value={`${stats.total_minutes || 0} min`} tid="stat-minutes" />
          <StatCard icon={CheckCircle2} label={t('dashboard.completed')} value={stats.completed_songs || 0} tid="stat-completed" />
          <StatCard icon={PlayCircle} label={t('dashboard.learning')} value={stats.learning_songs || 0} tid="stat-learning" />
        </div>

        <SongOfWeekHero />

        <section className="mb-10">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-3xl">{t('dashboard.recommended')}</h2>
            <Link to="/library" data-testid="see-all-songs" className="text-orange-400 text-sm hover:underline">{t('dashboard.see_all')}</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {songs.map((s, i) => (
              <Link
                key={s.id}
                to={`/song/${s.id}`}
                data-testid={`song-card-${i}`}
                className="card-stone overflow-hidden hover:border-orange-800/60 transition-colors group rise"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="aspect-[4/3] bg-stone-800 overflow-hidden">
                  {s.cover_url && (
                    <img src={s.cover_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                </div>
                <div className="p-4">
                  <div className="overline text-stone-500">{s.genre} · {s.difficulty}</div>
                  <div className="font-display text-xl mt-1">{s.title}</div>
                  <div className="text-stone-400 text-sm">{s.artist}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-3xl">{t('dashboard.your_badges')}</h2>
            <Link to="/profile" data-testid="see-all-badges" className="text-orange-400 text-sm hover:underline">{t('dashboard.see_all')}</Link>
          </div>
          <Badges badges={badges.slice(0, 6)} compact />
        </section>

        <section className="glass rounded-2xl p-8 flex items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-orange-700/20 border border-orange-800/40 flex items-center justify-center">
            <Sparkles className="text-orange-400" />
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl">{t('dashboard.stuck')}</div>
            <div className="text-stone-400 text-sm">{t('dashboard.ask_maestro')}</div>
          </div>
          <Link to="/tutor" data-testid="open-tutor-btn" className="btn-primary">{t('dashboard.open_tutor')}</Link>
        </section>
      </div>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, tid }) {
  return (
    <div data-testid={tid} className="card-stone p-5">
      <Icon size={18} className="text-orange-400 mb-3" />
      <div className="font-display text-3xl">{value}</div>
      <div className="text-stone-400 text-sm">{label}</div>
    </div>
  );
}
